<template>
  <modal name="modal-trip-history" class="trip-history" transition="pop-out" width="100%" height="100%" :focus-trap="true" :scrollable="true" :adaptive="true" :reset="true" @before-close="reset">
    <div class="flex flex-wrap sm:flex-nowrap justify-between items-center modal-header less-margin-bottom text-lg bg-primary-50 font-serif px-2 sm:px-0 py-2 sm:py-0">
      <LoadingSpinner :isLoading="isLoading" v-if="isLoading" />
      <div class="w-full sm:w-auto pl-0 sm:pl-3 py-1 sm:py-2 mb-2 sm:mb-0">
        <date-picker v-model="dateRange" :lang="datePickerLocale" :key="date_picker_langKey" type="date" range :editable="false" :clearable="false" @change="getRouteResult()" :disabled-date="disableFutureDate" class="w-full sm:w-auto"></date-picker>
      </div>
      <div class="flex-1 text-center sm:text-left">
        <div class="mt-0 sm:mt-2 text-black text-sm sm:text-base font-semibold truncate">{{ alarm.unit_name }}</div>
      </div>
      <div class="pr-0 sm:pr-2">
        <span class="close-button cursor-pointer" @click="hide()"><BaseIcon icon="times-circle" class="text-red-700 text-xl"/></span>
      </div>
    </div>
    <div class="modal-body less-side-padding border border-secondary-300 responsive-height" style="top: 0 !important;">
      <div class="flex flex-col lg:flex-row">
        <div class="relative w-full lg:w-3/12 xl:w-2/12 flex flex-col border border-secondary-300 order-2 lg:order-1">
          <div class="absolute w-full top-0 left-0 z-10 bg-white h-auto px-2">
            <div class="mt-2 border border-secondary-300 flex flex-col sm:flex-row justify-between px-2 py-2 gap-2">
              <div class="flex justify-center sm:justify-start gap-2">
                <tooltip message="See optimized route">
                  <div class="p-2 w-10 text-center" :class="optimizedPathIsReady ? 'btn-red' : 'btn-red-outline'" @click="getOptimizedRoute">
                    <BaseIcon icon="route" class="cursor-pointer text-base sm:text-lg" />
                  </div>
                </tooltip>
                <tooltip message="Change route color" :disabled="routeResults.length === 0">
                  <div class="p-2 w-10 text-center" :class="pathColorChanged ? 'btn-blue' : 'btn-blue-outline'" @click="ChangePathColor()">
                    <BaseIcon icon="palette" class="cursor-pointer text-base sm:text-lg" />
                  </div>
                </tooltip>
              </div>
              <div class="text-center sm:text-right">
                <div class="mt-0 sm:mt-2 text-xs sm:text-sm font-semibold">{{ formatDist(totalDist) }} km, {{ moment.duration(totalTripDuration, "seconds").humanize() }}</div>
              </div>
            </div>
          </div>

          <div class="pt-2 mt-20 sm:mt-16 px-2 overflow-auto border border-secondary-300 sidebar-responsive" ref="rightSidebar" style="padding-bottom:80px;margin-bottom:50px">
            <div v-for="(v, i) in routePlayedItems" :key="i" class="cursor-pointer mb-2">
              <div v-if="v.type == 'waiting'" class="bg-red-200 p-2" :class="[i == routePlayedItems.length - 1 ? 'border-4 border-primary-500' : '', currentPlayingIndex === i ? 'ring-2 ring-blue-500' : '']">
                <div class="flex justify-between items-center">
                  <span @click.stop="playIndividualItem(i)" class="cursor-pointer text-base sm:text-lg">
                    <BaseIcon v-if="currentPlayingIndex === i && isPlaying && !isPaused" icon="pause-circle" class="text-blue-500" />
                    <BaseIcon v-else icon="play-circle" class="text-blue-500" />
                  </span>
                  <span @click="focusRouteItem(v)" class="text-xs sm:text-sm text-gray-600 font-bold" :class="v.duration > 1800 ? 'text-red-700' : ''">Stop {{ v.order }}</span>
                </div>
                <div @click="focusRouteItem(v)">
                  <div class="font-bold text-left text-xs sm:text-sm" :class="v.duration > 1800 ? ' text-red-700' : ''">{{ v.address | formatAddress }}</div>
                  <div class="font-semibold">
                    <div class="text-right text-xs sm:text-sm" :class="v.duration > 1800 ? 'text-red-700' : ''">{{ moment.duration(v.duration, "seconds").humanize() }}</div>
                  </div>
                  <div class="">
                    <div class="text-right text-xxs sm:text-xs" :class="v.duration > 1800 ? 'font-bold text-red-700' : ''">{{ moment(v.startTime).format("HH:mm:ss") }} - {{ moment(v.endTime).format("HH:mm:ss") }}</div>
                  </div>
                </div>
              </div>
              <div v-if="v.type == 'trip'" :class="[i == routePlayedItems.length - 1 ? 'border-4 border-primary-500' : 'border border-gray-100', currentPlayingIndex === i ? 'ring-2 ring-blue-500' : '']">
                <div class="font-bold pt-1 pb-2 px-2 flex justify-between" :style="{ 'background-color': `${v.color}70` }" :class="i == routePlayedItems.length - 1 ? 'bg-primary-500' : ''">
                  <span @click.stop="playIndividualItem(i)" class="cursor-pointer text-base sm:text-lg">
                    <BaseIcon v-if="currentPlayingIndex === i && isPlaying && !isPaused" icon="pause-circle" class="text-blue-500" />
                    <BaseIcon v-else icon="play-circle" class="text-blue-500" />
                  </span>
                  <div class="text-xs sm:text-sm text-gray-900">Trip {{ v.order }}</div>
                </div>
                <div @click="focusRouteItem(v)">
                  <div class="text-xxs text-gray-600 mt-1 px-2 truncate">{{ v.startAddress | formatAddress }}</div>
                  <div class="text-xxs text-gray-600 px-2"><BaseIcon icon="arrow-down" class="text-xs" /></div>
                  <div class="text-xxs text-gray-600 px-2 truncate">{{ v.endAddress | formatAddress }}</div>
                  <div class="mt-1 px-2 text-xs sm:text-sm">
                    <span v-if="isPlaying && currentPlayingIndex === i">{{ (v.currentDist / 1000).toFixed(2) }} / </span><span class="font-semibold">{{ (v.distance / 1000).toFixed(2) }} km</span>
                  </div>
                  <div class="font-semibold px-2 text-xs sm:text-sm">
                    <div class="text-right">{{ moment.duration(v.duration, "seconds").humanize() }}</div>
                  </div>
                  <div class="mt-1 pb-2 px-2 flex justify-between text-xxs sm:text-xs">
                    <div class=""></div>
                    <div class="">{{ moment(v.startTime).format("HH:mm:ss") }} - {{ moment(v.endTime).format("HH:mm:ss") }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="sticky bottom-0 left-0 z-20 bg-white w-full px-2 py-2 shadow-md lg:absolute lg:bottom-1 lg:py-0 lg:shadow-none border-t lg:border-t-0 safe-area-inset-bottom">
            <div class="flex flex-col sm:flex-row justify-between items-center border border-secondary-300 p-3 gap-3 bg-white rounded-lg lg:rounded-none">
              <div class="flex justify-center sm:justify-start gap-4">
                <span @click="playAnimation()" class="cursor-pointer text-2xl active:scale-95 transition-transform">
                  <BaseIcon v-if="isPlaying && isPaused" icon="play-circle" class="text-blue-500" />
                  <BaseIcon v-else-if="!isPlaying" icon="play-circle" class="text-blue-500" />
                  <BaseIcon v-else icon="pause-circle" class="text-blue-500" />
                </span>
                <span class="cursor-not-allowed text-2xl text-gray-400 opacity-50">
                  <BaseIcon icon="step-forward" />
                </span>
                <span @click="stopAnimation()" class="cursor-pointer text-2xl active:scale-95 transition-transform">
                  <BaseIcon icon="stop-circle" class="text-gray-400" :class="{ 'text-red-500': stopPlay }" />
                </span>
              </div>
              <div class="flex-1 w-full sm:w-auto flex items-center text-xs px-2 sm:px-0 min-w-0">
                <input class="w-full cursor-pointer" :style="{ direction: '' }" type="range" min="0" max="500" step="10" v-model.number="playSpeed" />
              </div>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-9/12 xl:w-10/12 flex-col relative less-side-padding border border-secondary-300 order-1 lg:order-2">
          <transition name="fade">
            <div class="optimized-path" v-show="optimizedPathIsReady">
              <route_timeline />
            </div>
          </transition>
          <div class="sticky-info-panel" v-if="isPlaying">
            <div v-html="stickyInfoContent"></div>
          </div>
          <GmapMap :center="center" :zoom="zoom" map-type-id="terrain" class="w-full map-responsive" ref="tripHistoryMap">
            <GmapInfoWindow :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen" @closeclick="infoWinOpen = false"></GmapInfoWindow>
            <GmapCircle v-for="(pin, index) in trackedPoints" :key="index + 'c'" :center="pin.position" :visible="true" :options="pin.options" @mouseover="showInfoWindow($event, pin.infoText, true)" @mouseout="showInfoWindow($event, pin.infoText, false)"></GmapCircle>
            <GmapMarker :position="marker.position" :clickable="false" :draggable="false" :icon="marker.icon"></GmapMarker>
            <GmapMarker v-for="(m, index) in trackedPoints" :key="index" :position="m.position" :clickable="true" :draggable="false" :label="String(m.order)" @mouseover="showInfoWindow($event, m.infoText, true)" @mouseout="showInfoWindow($event, m.infoText, false)"></GmapMarker>
            <GmapPolyline v-for="(p, i) in trackedPaths" :key="i + 'p'" :path="p.path" :options="p.options" @mouseover="showInfoWindow($event, p.infoText, true, i)" @mouseout="showInfoWindow($event, p.infoText, false, i)"></GmapPolyline>
            <DirectionsRenderer :key="mapKey" travelMode="DRIVING" :origin="startPosition" :optimizeWaypoints="true" :destination="endPosition" :waypoints="waypoints" :options="{ draggable: false, zIndex: 99999, suppressMarkers: true, strokeColor: optimizedPathColor.activeColor, strokeWeight: 2 }" @directions_changed="directionChanged" @error_direction="handleDirectionError" />

            <template v-if="routeResults.length > 0 && trackedPoints.length == 0 && isPlaying === false">
              <template v-for="(m, index) in routeResults.filter((x) => x.type === 'waiting')">
                <GmapMarker :key="`S-${index}`" :position="m.position" :clickable="true" :draggable="false" :label="String(m.order)" @mouseover="showInfoWindow3($event, m.infoText, true)" @mouseout="showInfoWindow3($event, m.infoText, false)"></GmapMarker>
              </template>
              <GmapPolyline v-for="(rp, ri) in routeResults" :key="ri" :path="rp.path" :options="rp.options" @mouseover="showInfoWindow3($event, rp.infoText, true, ri)" @mouseout="showInfoWindow3($event, rp.infoText, false, ri)"></GmapPolyline>
            </template>
          </GmapMap>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/pl";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import { gmapApi } from "vue2-google-maps";
import tooltip from "@/components/base/tooltip";
import DirectionsRenderer from "@/components/map/DirectionsRenderer";
import route_timeline from "@/components/route_timeline";

export default {
  name: "TripTracker",

  components: {
    DatePicker,
    tooltip,
    DirectionsRenderer,
    route_timeline,
  },

  props: {},

  data() {
    return {
      zoom: this.$store.state.mapZoom,
      center: this.$store.state.mapCenter,
      dateRange: [new Date(this.moment().subtract(5, 'days').startOf("day")), new Date(this.moment().endOf("day"))],
      isLoading: false,
      startMarkerVisible: true,
      endMarkerVisible: true,
      startPosition: null,
      endPosition: null,
      departureTime: null,
      maxWaitingTime: 30,
      maxStops: 3,
      waypoints: [],
      savedWaypoints: [],
      expectedArrival: "",
      infoWindowPos: null,
      infoWinOpen: false,
      infoOptions: { content: "", icon: "", pixelOffset: { width: 0, height: -35 } },
      isPlaying: false,
      isPaused: false,
      stopPlay: false,
      playSpeed: 400,
      currentPlayingIndex: -1,
      totalDist: 0,
      totalTripDuration: 0,
      routeResults: [],
      trackedPoints: [],
      trackedHistoryPoints: [],
      historyTripsPoints: [],
      marker: {},
      trackedPaths: [],
      routePlayedItems: [],
      imei: "",
      mapIsReady: false,
      pathColor: { regularColor: "red", changedColor: "#1A8DE9", activeColor: "red" },
      optimizedPathColor: { regularColor: "#008000", changedColor: "red", activeColor: "#008000" },
      alarm: { unit_name: "" },
      imei_number: "",
      optimizedPathIsReady: false,
      pathColorChanged: false,
      mapKey: Date.now(),
      mapStartPosition: null, //for map bund
      mapEndPosition: null, //for map bund
      stickyInfoContent: null,
      date_picker_lang: this.$i18n.locale,
      date_picker_langKey: 0,
    };
  },
  computed: {
    google: gmapApi,
    actualDelay() {
      const maxSpeed = 500;
      const exponent = 3;
      const minDelay = 10;
      const maxDelay = 500;
      const speedRatio = this.playSpeed / maxSpeed;
      const delay = maxDelay * Math.pow(1 - speedRatio, exponent);
      return Math.max(minDelay, delay);
    },
    datePickerLocale() {
      // Map 'no' to 'nb' for date picker (Norwegian Bokmål)
      return this.date_picker_lang === 'no' ? 'nb' : this.date_picker_lang;
    },
  },
  filters: {
    formatAddress: function(value) {
      if (!value) return "";
      value = value.toString();
      return value.substring(0, value.lastIndexOf(","));
    },
  },
  watch: {
    "$i18n.locale"(newLocale) {
      this.date_picker_lang = newLocale;
      this.date_picker_langKey++;
    },
  },

  methods: {
    async getRouteResult() {
      //console.log("getRouteResult::imei_number==>", this.imei_number);
      try {
        this.zoom = this.$store.state.mapZoom;
        this.center = this.$store.state.mapCenter;
        this.pathColorChanged = false;

        this.trackedPoints = []; // waiting stops
        this.trackedPaths = []; // trips
        this.routePlayedItems = [];
        // play route animation reset
        this.isPlaying = false;
        this.stopPlay = false;
        this.infoWinOpen = false;
        this.infoOptions.content = null;
        // reset optimize route
        this.optimizedPathIsReady = false;
        this.startPosition = null;
        this.endPosition = null;
        this.waypoints = [];
        this.mapKey = Date.now();
        // end optimize route
        this.isLoading = true;
        this.routeResults = [];
        this.routePlayedItems = [];
        this.trackedPoints = [];
        this.trackedPaths = [];
        this.trackedHistoryPoints = [];
        this.historyTripsPoints = [];
        this.waypoints = [];
        this.startPosition = null;
        this.endPosition = null;

        let start = new Date(this.moment(this.dateRange[0]).startOf("day")).toISOString();
        let end = new Date(this.moment(this.dateRange[1]).endOf("day")).toISOString();

        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/secure-route-analytics/${this.imei_number}/${start}/${end}`);

        this.routeResults = response.data.routeResults;

        if (this.routeResults.length > 0) {
          this.routePlayedItems = this.routeResults;
          this.alarm = response.data.alarm;

          const firstElement = this.routeResults[0];
          if (firstElement.type === "trip") {
            //this.startPosition = firstElement.startPosition;
            this.mapStartPosition = firstElement.startPosition;
          }

          if (firstElement.type === "waiting") {
            //this.startPosition = firstElement.position;
            this.mapStartPosition = firstElement.position;
          }

          const lastElement = this.routeResults[this.routeResults.length - 1];
          if (lastElement.type === "trip") {
            //this.endPosition = lastElement.startPosition;
            this.mapEndPosition = lastElement.startPosition;
          }

          if (lastElement.type === "waiting") {
            this.mapEndPosition = lastElement.position;
          }

          this.setMapBounds();
          await this.reverseGeocodeAddresses();
        }

        this.mapIsReady = true;
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.mapIsReady = true;
        this.handleError(error);
      }
    },
    getOptimizedRoute() {
      this.optimizedPathIsReady = !this.optimizedPathIsReady;

      if (!this.optimizedPathIsReady) {
        this.startPosition = null;
        this.endPosition = null;
        this.waypoints = [];
        this.mapKey = Date.now();
      }

      if (this.optimizedPathIsReady && this.waypoints.length === 0) {
        const firstElement = this.routeResults[0];
        if (firstElement.type === "trip") {
          this.startPosition = firstElement.startPosition;
          this.mapStartPosition = firstElement.startPosition;
        }

        if (firstElement.type === "waiting") {
          this.startPosition = firstElement.position;
          this.mapStartPosition = firstElement.position;
        }

        const lastElement = this.routeResults[this.routeResults.length - 1];

        if (lastElement.type === "trip") {
          this.endPosition = lastElement.startPosition;
          this.mapEndPosition = lastElement.startPosition;
        }

        if (lastElement.type === "waiting") {
          this.endPosition = lastElement.position;
          this.mapEndPosition = lastElement.position;
        }

        let wayPoints = this.routeResults.slice(1, -1);
        wayPoints.forEach((r) => {
          if (r.type === "waiting") {
            this.waypoints.push({ location: r.position });
          }
        });
      }
    },
    directionChanged(data) {
      let waypoints = [];
      for (let i = 0; i < data.routes[0].legs[0].via_waypoints.length; i++) {
        let w = data.routes[0].legs[0].via_waypoints[i];
        waypoints.push({ lat: w.lat(), lng: w.lng() });
      }

      this.savedWaypoints = waypoints;
      this.calculateExpectedArrival();
      this.$emit("routeChanged", this.definedRoute);
    },
    setMapBounds() {
      var vm = this;

      //setTimeout(() => {
      this.$nextTick(() => {
        vm.$refs.tripHistoryMap.$mapPromise.then((map) => {
          const bounds = new vm.google.maps.LatLngBounds();

          if (vm.mapStartPosition) bounds.extend(vm.mapStartPosition);
          if (vm.mapEndPosition) bounds.extend(vm.mapEndPosition);

          map.fitBounds(bounds);
          vm.zoom = 12;
        });
      });

      //}, 500);
    },

    async reverseGeocodeAddresses() {
      let vm = this;
      this.routeResults.forEach(async (r) => {
        // todo: use same address for lastWaitingStop & tripStartAddress, tripEndAdress & waitingStop
        // Assign random color to trips from the start
        const tripColor = r.type === "trip" ? this.getRandomPathColor() : "red";
        r.color = tripColor;
        r.options = {
          strokeColor: tripColor,
          strokeOpacity: 1,
          strokeWeight: 3,
        };
        if (r.type == "trip") {
          let startAddress = await this.reverseGeocode(r.startPosition);
          let endAddress = await this.reverseGeocode(r.endPosition);

          r.startAddress = startAddress;
          r.endAddress = endAddress;

          r.infoText = `<b>Trip ${r.order}</b><br>
                    ${vm.formatAdd(r.startAddress)} -> ${vm.formatAdd(r.endAddress)}<br>
                    ${vm.moment(r.startTime).format("HH:mm:ss")} -> ${vm.moment(r.endTime).format("HH:mm:ss")}, ${vm.moment.duration(r.duration, "seconds").humanize()}<br>
                    ${vm.formatDist(r.distance)} km<br>`;
        } else if (r.type == "waiting") {
          let address = await this.reverseGeocode(r.position);
          r.address = address;
          r.infoText = `<b>Stop ${r.order}</b><br>
                    <b>${vm.formatAdd(r.address)}</b><br>
                    ${vm.moment(r.startTime).format("HH:mm")} - ${vm.moment(r.endTime).format("HH:mm")}, ${vm.moment.duration(r.duration, "seconds").humanize()}`;
        }
      });
    },

    async reverseGeocode(point) {
      let address = "";

      if (!point || typeof point.lat === "undefined" || typeof point.lng === "undefined") {
        return address;
      }

      try {
        const geocoder = new this.google.maps.Geocoder();
        let res = await geocoder.geocode({ location: point });

        if (!res.results || res.results.length === 0) {
          return address;
        }

        // Plus Code pattern - matches formats like "Q9W3+6FR" anywhere in the string
        const plusCodePattern = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,4}/i;

        // Preferred address types in order of priority
        const preferredTypes = [
          "street_address",
          "premise",
          "subpremise",
          "route",
          "intersection",
          "neighborhood",
          "sublocality",
          "locality",
          "administrative_area_level_3",
          "administrative_area_level_2",
        ];

        // First, try to find an address with preferred types that doesn't have Plus Code
        for (let type of preferredTypes) {
          for (let result of res.results) {
            if (result.types && result.types.includes(type)) {
              if (!plusCodePattern.test(result.formatted_address)) {
                return result.formatted_address;
              }
            }
          }
        }

        // Second pass: find any result without Plus Code
        for (let result of res.results) {
          if (!plusCodePattern.test(result.formatted_address)) {
            return result.formatted_address;
          }
        }

        // Third pass: try to clean Plus Code from the beginning of address
        for (let result of res.results) {
          let cleaned = result.formatted_address.replace(/^[A-Z0-9]{4,8}\+[A-Z0-9]{2,4},?\s*/i, "").trim();
          if (cleaned && cleaned.length > 5) {
            return cleaned;
          }
        }

        // Final fallback: return first result as-is
        address = res.results[0].formatted_address;
      } catch (error) {
        console.log("Reverse geocode error:", error);
      }

      return address;
    },

    processRoutePlayedItems() {
      this.routePlayedItems = [];

      for (let i = 0; i < this.routeResults.length; i++) {
        let r = this.routeResults[i];
        this.routePlayedItems.push(r);
      }
    },
    async playRouteSequence() {
      this.trackedPoints = []; // Reset waiting stops
      this.trackedPaths = []; // Reset trips
      this.routePlayedItems = [];
      this.totalDist = 0; // Reset total distance for selected period
      this.totalTripDuration = 0;

      this.marker = {
        position: null,
        icon: { path: this.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#1A8DE9", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
      };

      for (let i = 0; i < this.routeResults.length; i++) {
        let r = this.routeResults[i];

        this.routePlayedItems.push(r);
        this.scrollToBottom();

        let infoText = "";
        r.currentDist = 0;

        if (this.stopPlay) {
          this.isPlaying = false;
          this.isPaused = false;
          this.stopPlay = false;
          this.processRoutePlayedItems();
          return;
        }

        if (r.type === "trip") {
          // Zoom map to fit this trip's bounds with smooth animation
          this.zoomToTripBounds(r);
          
          // Brief pause before starting trip for visual transition
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Update marker color for active trip
          this.marker.icon.fillColor = "#1A8DE9";

          this.trackedPaths.push({
            path: [],
            geodesic: true,
            options: {
              strokeColor: "#1A8DE9",
              strokeOpacity: 1,
              strokeWeight: 4,
              zIndex: 999,
              icons: [{ icon: { path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 }, offset: "100%", repeat: "700px" }],
            },
            infoText: "",
          });

          for (let j = 0; j < r.path.length; j++) {
            while (this.isPaused) {
              await new Promise((resolve) => setTimeout(resolve, 100)); // Pause functionality
            }

            if (this.stopPlay) {
              this.isPlaying = false;
              this.isPaused = false;
              this.stopPlay = false;
              this.processRoutePlayedItems();
              return;
            }

            let point = r.path[j];

            // Constructing infoText for each point
            infoText = `<b>Trip ${r.order}</b><br>
              ${this.formatAdd(r.startAddress)} → ${this.formatAdd(r.endAddress)}<br>
              ${this.moment(r.startTime).format("HH:mm")} → ${this.moment(r.endTime).format("HH:mm")}, ${this.moment.duration(r.duration, "seconds").humanize()}, ${this.formatDist(r.distance)} km<br>
              <b>${this.moment(point.time).format("HH:mm:ss")} | ${this.formatDist(point.dist)} km | ${this.formatSpeed(point.speed)} km/h</b>`;

            this.trackedPaths[this.trackedPaths.length - 1].path.push(point);

            if (!this.stopPlay) {
              await new Promise((resolve) => setTimeout(resolve, this.actualDelay));
            }

            // Update marker position and pan map to follow
            this.marker.position = point;
            this.panToMarker(point);
            this.stickyInfoContent = infoText;
            r.currentDist = point.dist;
          }

          // Finalizing each trip - use existing color from initial load
          let lastPathIndex = this.trackedPaths.length - 1;
          this.trackedPaths[lastPathIndex].options.strokeColor = r.color;
          this.trackedPaths[lastPathIndex].infoText = infoText;
          r.infoText = infoText;
          r.position = r.startPosition;

          this.totalDist += r.distance;
          this.totalTripDuration += r.duration;

          // Brief pause after trip completes
          await new Promise((resolve) => setTimeout(resolve, 300));

        } else if (r.type == "waiting") {
          // Change marker to stop color
          this.marker.icon.fillColor = "#EF4444";
          this.marker.position = r.position;

          // Zoom to waiting position
          this.zoomToPosition(r.position, 16);
          
          // Brief pause for visual transition
          await new Promise((resolve) => setTimeout(resolve, 400));

          infoText = `<b>Stop ${r.order}</b><br>
            <b>${this.formatAdd(r.address)}</b><br>
            ${this.moment(r.startTime).format("HH:mm")} - ${this.moment(r.endTime).format("HH:mm")}<br>
            ${this.moment.duration(r.duration, "seconds").humanize()}`;

          this.stickyInfoContent = infoText;

          this.trackedPoints.push({
            ...r,
            options: {
              fillColor: "#EF4444",
              fillOpacity: 0.3,
              strokeColor: "#EF4444",
              strokeOpacity: 1,
              strokeWeight: 3,
              radius: 60,
            },
            infoText: infoText,
          });

          // Longer pause at stops to show waiting time visually
          const stopPauseDuration = Math.min(r.duration / 10, 2000); // Scale with actual wait time, max 2 seconds
          if (!this.stopPlay) {
            await new Promise((resolve) => setTimeout(resolve, Math.max(stopPauseDuration, 500)));
          }
        }
      }

      this.isPlaying = false;
      this.stickyInfoContent = `<b>Playback Complete</b><br>Total: ${this.formatDist(this.totalDist)} km, ${this.moment.duration(this.totalTripDuration, "seconds").humanize()}`;
      
      // Auto-hide sticky info after 3 seconds
      setTimeout(() => {
        if (!this.isPlaying) {
          this.stickyInfoContent = null;
        }
      }, 3000);
    },

    scrollToBottom() {
      this.$nextTick(function() {
        let el = this.$refs.rightSidebar;
        el.scrollTop = el.scrollHeight;
      });
    },

    zoomToTripBounds(trip) {
      if (!trip.path || trip.path.length === 0) return;

      const bounds = new this.google.maps.LatLngBounds();
      
      // Include start and end positions
      if (trip.startPosition) bounds.extend(trip.startPosition);
      if (trip.endPosition) bounds.extend(trip.endPosition);
      
      // Include all path points for accurate bounds
      trip.path.forEach((point) => {
        if (point.lat && point.lng) {
          bounds.extend({ lat: point.lat, lng: point.lng });
        }
      });

      this.$refs.tripHistoryMap.$mapObject.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    },

    zoomToPosition(position, zoomLevel = 15) {
      if (!position) return;
      this.$refs.tripHistoryMap.$mapObject.panTo(position);
      this.$refs.tripHistoryMap.$mapObject.setZoom(zoomLevel);
    },

    panToMarker(point) {
      if (!point) return;
      const map = this.$refs.tripHistoryMap.$mapObject;
      const bounds = map.getBounds();
      
      if (bounds) {
        const position = new this.google.maps.LatLng(point.lat, point.lng);
        // Only pan if marker is near edge of visible area
        if (!bounds.contains(position)) {
          map.panTo(position);
        }
      }
    },

    getRandomPathColor() {
      // Generate blue-ish colors only
      const blueColors = [
        "#1E40AF", // Blue 800
        "#1D4ED8", // Blue 700
        "#2563EB", // Blue 600
        "#3B82F6", // Blue 500
        "#0EA5E9", // Sky 500
        "#0284C7", // Sky 600
        "#0369A1", // Sky 700
        "#0891B2", // Cyan 600
        "#0E7490", // Cyan 700
        "#06B6D4", // Cyan 500
        "#14B8A6", // Teal 500
        "#0D9488", // Teal 600
        "#0F766E", // Teal 700
        "#6366F1", // Indigo 500
        "#4F46E5", // Indigo 600
        "#4338CA", // Indigo 700
        "#7C3AED", // Violet 600
        "#8B5CF6", // Violet 500
      ];
      return blueColors[Math.floor(Math.random() * blueColors.length)];
    },

    ChangePathColor() {
      this.pathColorChanged = !this.pathColorChanged;
      // this.pathColor.activeColor = this.pathColor.activeColor === this.pathColor.regularColor ? this.pathColor.changedColor : this.pathColor.regularColor;
      // this.optimizedPathColor.activeColor = this.optimizedPathColor.activeColor === this.optimizedPathColor.regularColor ? this.optimizedPathColor.changedColor : this.optimizedPathColor.regularColor;

      if (this.pathColorChanged) {
        const colors = this.routeResults.map(() => this.getRandomPathColor());
        this.routeResults.forEach((r, index) => {
          const color = colors[index];
          r.options = {
            strokeColor: color,
            strokeOpacity: 1,
            strokeWeight: 3,
          };
        });
      } else {
        this.routeResults.forEach((r) => {
          r.options = {
            strokeColor: "red",
            strokeOpacity: 1,
            strokeWeight: 3,
          };
        });
      }
    },

    async changeLocationEnd(event) {
      try {
        let position = { lat: event.latLng.lat(), lng: event.latLng.lng() };

        const geocoder = new this.google.maps.Geocoder();
        let response = await geocoder.geocode({ location: position });

        this.$refs.endPlace.$refs.input.value = response.results[0].formatted_address;
      } catch (error) {
        console.log(error);
      }
    },

    calculateExpectedArrival() {
      if (this.totalDuration) {
        let totalWaitingTime = parseInt(this.maxWaitingTime) * parseInt(this.maxStops);
        this.expectedArrival = this.moment(this.departureTime)
          .add(totalWaitingTime, "minutes")
          .add(this.totalDuration, "seconds")
          .format("YYYY-MM-DD, HH:mm");
      }
    },

    handleDirectionError(status) {
      if (status == "ZERO_RESULTS") {
        this.handleError(null, "Ingen direkt rutt", "top-right", 3000);
        this.startMarkerVisible = true;
        this.endMarkerVisible = true;
      }

      if (status == "INVALID_REQUEST") {
        this.handleError(null, "Avgångstid måste vara i framtiden", "top-right", 1500);
        this.checkDepartureTime();
      }
    },

    copyDirectionLink() {
      var vm = this;
      let origin = `${this.startPosition.lat},${this.startPosition.lng}`;
      let destination = `${this.endPosition.lat},${this.endPosition.lng}`;
      let waypoints = "";
      for (let i = 0; i < this.savedWaypoints.length; i++) {
        let w = this.savedWaypoints[i];
        waypoints += `${w.lat},${w.lng}|`;
      }

      let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving&waypoints=${waypoints}`;

      const cb = navigator.clipboard;
      cb.writeText(url).then(() => vm.handleSuccess(this.$t('success_messages.directions_copied')));
    },
    async getFormattedAddress(position) {
      try {
        //let position = { lat: location.lat(), lng: event.latLng.lng() };

        const geocoder = new this.google.maps.Geocoder();
        let response = await geocoder.geocode({ location: position });

        return response.results[0].formatted_address;
      } catch (error) {
        console.log(error);
      }
    },

    disableDate(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },

    formatAdd: function(value) {
      if (!value) return "";
      value = value.toString();
      return value.substring(0, value.indexOf(","));
    },

    formatDist(value) {
      return (value / 1000).toFixed(2);
    },

    formatSpeed(value) {
      return Math.ceil(value);
    },

    disableTime(date) {
      let today = this.moment();
      today.subtract(1, "hour");
      return date < new Date(today);
    },

    handleTimeSelection(value, type) {
      // eslint-disable-line
      if (type === "minute") {
        this.datePickerOpen = false;
      }

      this.checkDepartureTime();
      this.calculateExpectedArrival();
    },

    checkDepartureTime() {
      let departureTimeMoment = this.moment(this.departureTime);
      let now = this.moment();
      if (departureTimeMoment.isBefore(now)) {
        let t = 15 - (now.minutes() % 15);
        now.add(t, "minutes");
        this.departureTime = new Date(now);
      }
    },

    initDate() {
      this.departureTime = new Date(
        this.moment()
          .add(2, "hours")
          .startOf("hour")
      );
    },

    stopAnimation() {
      this.stopPlay = !this.stopPlay;

      if (this.stopPlay) {
        this.isPlaying = false;
        this.isPaused = false;
        this.playRouteSequence();
        this.stopPlay = false;
      }
    },
    playAnimation() {
      this.stopPlay = false;
      this.currentPlayingIndex = -1; // Reset individual play index when using global play
      // If not currently playing, start the animation
      if (!this.isPlaying && !this.isPaused) {
        this.isPlaying = true;
        this.isPaused = false;
        this.playRouteSequence();
      } else if (this.isPlaying && !this.isPaused) {
        // If currently playing and not paused, pause the animation
        this.isPaused = true;
      } else if (this.isPlaying && this.isPaused) {
        // If currently paused, resume the animation
        this.isPaused = false;
      }
    },

    async playIndividualItem(index) {
      const item = this.routeResults[index];
      if (!item) return;

      // If clicking on the same item that's playing, toggle pause
      if (this.currentPlayingIndex === index && this.isPlaying) {
        this.isPaused = !this.isPaused;
        return;
      }

      // Stop any current playback
      if (this.isPlaying) {
        this.stopPlay = true;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // Reset state
      this.stopPlay = false;
      this.isPlaying = true;
      this.isPaused = false;
      this.currentPlayingIndex = index;

      // Initialize marker
      this.marker = {
        position: null,
        icon: { path: this.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#1A8DE9", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
      };

      if (item.type === "trip") {
        await this.playIndividualTrip(item, index);
      } else if (item.type === "waiting") {
        await this.playIndividualStop(item);
      }

      this.isPlaying = false;
      this.currentPlayingIndex = -1;
    },

    async playIndividualTrip(trip, index) {
      // Zoom to trip bounds
      this.zoomToTripBounds(trip);
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Update marker color
      this.marker.icon.fillColor = "#1A8DE9";

      // Create path for this trip
      const tripPath = {
        path: [],
        geodesic: true,
        options: {
          strokeColor: "#1A8DE9",
          strokeOpacity: 1,
          strokeWeight: 4,
          zIndex: 999,
          icons: [{ icon: { path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 }, offset: "100%", repeat: "700px" }],
        },
        infoText: "",
      };

      // Add to tracked paths temporarily
      const pathIndex = this.trackedPaths.length;
      this.trackedPaths.push(tripPath);

      trip.currentDist = 0;

      for (let j = 0; j < trip.path.length; j++) {
        while (this.isPaused) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        if (this.stopPlay || this.currentPlayingIndex !== index) {
          return;
        }

        let point = trip.path[j];

        const infoText = `<b>Trip ${trip.order}</b><br>
          ${this.formatAdd(trip.startAddress)} → ${this.formatAdd(trip.endAddress)}<br>
          ${this.moment(trip.startTime).format("HH:mm")} → ${this.moment(trip.endTime).format("HH:mm")}, ${this.moment.duration(trip.duration, "seconds").humanize()}, ${this.formatDist(trip.distance)} km<br>
          <b>${this.moment(point.time).format("HH:mm:ss")} | ${this.formatDist(point.dist)} km | ${this.formatSpeed(point.speed)} km/h</b>`;

        this.trackedPaths[pathIndex].path.push(point);

        if (!this.stopPlay) {
          await new Promise((resolve) => setTimeout(resolve, this.actualDelay));
        }

        this.marker.position = point;
        this.panToMarker(point);
        this.stickyInfoContent = infoText;
        trip.currentDist = point.dist;
      }

      // Finalize trip - use existing color from initial load
      this.trackedPaths[pathIndex].options.strokeColor = trip.color;

      this.stickyInfoContent = `<b>Trip ${trip.order} Complete</b><br>${this.formatDist(trip.distance)} km, ${this.moment.duration(trip.duration, "seconds").humanize()}`;

      setTimeout(() => {
        if (!this.isPlaying) {
          this.stickyInfoContent = null;
        }
      }, 3000);
    },

    async playIndividualStop(stop) {
      // Change marker to stop color
      this.marker.icon.fillColor = "#EF4444";
      this.marker.position = stop.position;

      // Zoom to stop
      this.zoomToPosition(stop.position, 16);
      await new Promise((resolve) => setTimeout(resolve, 400));

      const infoText = `<b>Stop ${stop.order}</b><br>
        <b>${this.formatAdd(stop.address)}</b><br>
        ${this.moment(stop.startTime).format("HH:mm")} - ${this.moment(stop.endTime).format("HH:mm")}<br>
        ${this.moment.duration(stop.duration, "seconds").humanize()}`;

      this.stickyInfoContent = infoText;

      // Add to tracked points
      this.trackedPoints.push({
        ...stop,
        options: {
          fillColor: "#EF4444",
          fillOpacity: 0.3,
          strokeColor: "#EF4444",
          strokeOpacity: 1,
          strokeWeight: 3,
          radius: 60,
        },
        infoText: infoText,
      });

      // Wait for visual
      const stopPauseDuration = Math.min(stop.duration / 10, 2000);
      await new Promise((resolve) => setTimeout(resolve, Math.max(stopPauseDuration, 1000)));

      setTimeout(() => {
        if (!this.isPlaying) {
          this.stickyInfoContent = null;
        }
      }, 3000);
    },

    setupTripInfo() {
      //var vm = this;
    },
    reset() {
      this.startLocation = null;
      this.endLocation = null;
      this.routeResults = [];
      this.routePlayedItems = [];
      this.trackedPoints = [];
      this.trackedPaths = [];
      this.trackedHistoryPoints = [];
      this.historyTripsPoints = [];
      this.alarm.unit_name = "";
      this.currentPlayingIndex = -1;
      this.isPlaying = false;
      this.isPaused = false;
      this.stopPlay = false;
    },

    isNumber(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    showTripReport(log) {
      let logType = "";
      if (log.type == "trip_start") logType = "Resan börjar";
      else if (log.type == "trip_end") logType = "Resan genomförd";
      else if (log.type == "waiting_start") logType = "Väntan började";
      else if (log.type == "waiting_end") logType = "Väntande slut";
      else if (log.type == "on_track") logType = "På rutt";
      else if (log.type == "deviated") logType = "Avvikit från rutt";

      let infoText = `
        ${logType}<br>
        ${this.formatDate(log.time)}
      `;
      if (log.type == "waiting_end") infoText += `<br>${log.duration} min.`;

      this.showInfoWindow(log.position, infoText, true);
      this.zoomPosition(log.position);
    },

    showInfoWindow2(position, infoText, show) {
      this.infoWindowPos = position;
      this.infoOptions.content = infoText;
      this.infoWinOpen = show;
    },

    showInfoWindow(event, infoText, show, index = -1) {
      if (show) {
        // set
        this.infoWindowPos = event.latLng;
        this.infoOptions.content = infoText;
        this.infoWinOpen = show;
        if (index > -1) {
          this.trackedPaths[index].options.strokeWeight = 4;
          this.trackedPaths[index].options.strokeOpacity = 1;

          for (let i = 0; i < this.trackedPaths.length; i++) {
            let p = this.trackedPaths[i];
            if (i != index) {
              p.options.strokeOpacity = 0.2;
              p.options.strokeWeight = 2.5;
            }
          }
        }
      } else {
        // reset
        this.infoWinOpen = show;
        for (let i = 0; i < this.trackedPaths.length; i++) {
          let p = this.trackedPaths[i];
          p.options.strokeOpacity = 1;
          p.options.strokeWeight = 2.5;
        }
      }
    },

    showInfoWindow3(event, infoText, show, index = -1) {
      if (show) {
        // set
        this.infoWindowPos = event.latLng;
        this.infoOptions.content = infoText;
        this.infoWinOpen = show;
        if (index > -1) {
          const selectedRoute = this.routeResults[index];
          const updatedOptions = {
            ...selectedRoute.options,
            strokeWeight: 4,
            strokeOpacity: 1,
          };
          this.routeResults[index].options = updatedOptions;
          for (let i = 0; i < this.routeResults.length; i++) {
            if (i !== index) {
              const otherRoute = this.routeResults[i];
              const updatedOptions = {
                ...otherRoute.options,
                strokeWeight: 3,
                strokeOpacity: 0.2,
              };
              this.routeResults[i].options = updatedOptions;
            }
          }
        }
      } else {
        // reset
        this.infoWinOpen = show;
        this.routeResults.forEach((p) => {
          p.options = {
            ...p.options,
            strokeWeight: 3,
            strokeOpacity: 1,
          };
        });
      }
    },

    focusRouteItem(point) {
      var vm = this;
      if (point && point.position) {
        setTimeout(() => {
          vm.$refs.tripHistoryMap.$mapObject.panTo(point.position);
          vm.$refs.tripHistoryMap.$mapObject.setZoom(13);

          if (point.type == "trip") {
            vm.showInfoWindow2({ latLng: point.position }, point.infoText, true, point.order - 1);
          } else {
            vm.showInfoWindow2({ latLng: point.position }, point.infoText, true);
            for (let i = 0; i < this.trackedPaths.length; i++) {
              let p = this.trackedPaths[i];
              p.options.strokeOpacity = 1;
              p.options.strokeWeight = 2.5;
            }
          }
        }, 300);
      }
    },

    zoomPosition(position) {
      var vm = this;
      setTimeout(() => {
        vm.$refs.tripHistoryMap.$mapObject.panTo(position);
        vm.$refs.tripHistoryMap.$mapObject.setZoom(13);
      }, 300);
    },
    getGSMSignalPercent(g, type) {
      if (new RegExp(/^TELTONIKA/g).test(type)) {
        return g * 20 + "%";
      }
      {
        // conversion as per SRT documentation
        if (g <= 31) return Math.ceil(3.225 * g) + " %";
        else return "N/A";
      }
    },

    setupModal(alram_data) {
      this.initDate();
      this.isPlaying = false;
      this.infoWinOpen = false;
      this.infoOptions.content = null;
      this.dateRange = [new Date(this.moment().subtract(5, 'days').startOf("day")), new Date(this.moment().endOf("day"))];
      this.zoom = this.$store.state.mapZoom;
      this.center = this.$store.state.mapCenter;
      this.alarm.unit_name = alram_data.unit_name;
      this.imei_number = alram_data.imei_number;

      this.routeResults = [];
      this.routePlayedItems = [];
      this.trackedPoints = [];
      this.trackedPaths = [];
      this.trackedHistoryPoints = [];
      this.historyTripsPoints = [];
      this.waypoints = [];
      this.startPosition = null;
      this.endPosition = null;

      this.getRouteResult();
    },
    hide() {
      this.$modal.hide("modal-trip-history");
    },
    disableFutureDate(date) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return date > today;
    },
  },

  created() {},

  mounted() {},
};
</script>
<style scoped>
.trip-history {
  overflow-x: hidden;
}

.trip-history ::v-deep .vm--modal {
  overflow-x: hidden;
}

/* Safe area inset for iPhone notch and home indicator */
.safe-area-inset-bottom {
  padding-bottom: env(safe-area-inset-bottom);
}

/* Responsive sidebar heights */
.sidebar-responsive {
  height: 40vh;
  min-height: 300px;
}

@media (min-width: 768px) {
  .sidebar-responsive {
    height: 60vh;
  }
}

@media (min-width: 1024px) {
  .sidebar-responsive {
    height: 80vh;
  }
}

/* Responsive map heights */
.map-responsive {
  height: 50vh;
  min-height: 400px;
}

@media (min-width: 768px) {
  .map-responsive {
    height: 70vh;
  }
}

@media (min-width: 1024px) {
  .map-responsive {
    height: 100%;
    min-height: 80vh;
  }
}

.max-height-80vh {
  max-height: 80vh;
  overflow-y: auto; /* Enable scrolling */
}

.route-tab {
  max-height: 640px;
}
.route-summary {
  max-height: 400px;
}
.optimized-path {
  position: absolute;
  width: 100%;
  left: 0px;
  z-index: 9999;
  margin-top: 60px;
  border: 2px solid #ddd;
  min-height: 50vh;
  max-height: 50vh;
  background: #fff;
  padding: 10px;
  overflow-y: scroll;
}

@media (min-width: 640px) {
  .optimized-path {
    width: 22rem;
    min-height: 70vh;
    max-height: 70vh;
  }
}

@media (min-width: 1024px) {
  .optimized-path {
    width: 25rem;
    min-height: 83vh;
    max-height: 83vh;
  }
}

/* Transition styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.sticky-info-panel {
  width: 90%;
  max-width: 380px;
  position: fixed;
  top: 15%;
  left: 50%;
  transform: translate(-50%, 0);
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 10px 12px;
  z-index: 1000;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 12px;
  line-height: 1.4;
  animation: slideIn 0.3s ease-out;
}

@media (min-width: 640px) {
  .sticky-info-panel {
    top: 12%;
    left: 55%;
    transform: translate(-50%, -50%);
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.5;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translate(-50%, -20%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@media (min-width: 640px) {
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -60%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
